import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { changeavailable, changeStatus, getUserById } from 'helpers/requests';
import { resolveImage } from 'helpers/config';
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  Label,
  Input,
  FormGroup,
  UncontrolledTooltip,
  Table,
  Button,
  Badge
} from 'reactstrap';
import swal from 'sweetalert';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Image1 from '../../assets/images/users/avatar.png'

const SingleUser = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchUser = async () => {
    try {
      const response = await getUserById(id);
      console.log(response)
      setUser(response);
    } catch (error) {
      console.error("Error fetching user:", error);
      swal("Error", "Unable to fetch user details. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  const handleCheckRequest = (request) => {
    // Redirect to the TicketDetails page with the ticket ID
   
    window.location.href =`/support-tickets/${request.id}`
  };

  const toggleAvailability = async () => {
    setUpdating(true);
    try {
      await changeavailable(id);
      swal("Success", "User availability has been updated!", "success");
      fetchUser(); // Refresh the user data
    } catch (error) {
      console.error("Error toggling availability:", error);
      swal("Error", "Unable to update availability. Please try again later.", "error");
    } finally {
      setUpdating(false);
    }
  };

  const reservationColumns = useMemo(
    () => [
      {
        header: "Reservation ID",
        accessorKey: "id",
        enableColumnFilter: false,
      },
      {
        header: "Surnom",
        accessorKey: "surnom",
        enableColumnFilter: false,
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
      },
      {
        header: "Check-in Date",
        accessorKey: "checkin_date",
        cell: (cellProps) => new Date(cellProps.row.original.checkin_date).toLocaleDateString(),
        enableColumnFilter: false,
      },
      {
        header: "Check-out Date",
        accessorKey: "checkout_date",
        cell: (cellProps) => new Date(cellProps.row.original.checkout_date).toLocaleDateString(),
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
      },
      {
        header: "Total Amount",
        accessorKey: "totalAmount",
        cell: (cellProps) => `${cellProps.row.original.totalAmount} DA`,
        enableColumnFilter: false,
      }
    ],
    []
  );

  const supportTicketColumns = useMemo(
    () => [
      {
        header: "Subject",
        accessorKey: "subject",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Admin In Charge",
        accessorKey: "adminUsername",
        cell: (cellProps) =>
          cellProps.row.original.adminUsername || "Not assigned yet",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: (cellProps) =>
          new Date(cellProps.row.original.created_at).toLocaleDateString(),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "state",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const stateMap = {
            OPEN: { text: "Open", color: "danger" },
            CLOSED: { text: "Closed", color: "success" },
          };
          const stateInfo = stateMap[cellProps.row.original.state] || {
            text: "Unknown",
            color: "secondary",
          };
          return (
            <Badge pill className={`badge-soft-${stateInfo.color} ms-1`}>
              {stateInfo.text}
            </Badge>
          );
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              onClick={() => handleCheckRequest(cellProps.row.original)}
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`checktooltip-${cellProps.row.original.id}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`checktooltip-${cellProps.row.original.id}`}
              >
                View Details
              </UncontrolledTooltip>
            </Link>
            
          </div>
        ),
      },
    ],
    []
  );
  

  const visaColumns = useMemo(
    () => [
      {
        header: "Visa ID",
        accessorKey: "id",
        enableColumnFilter: false,
      },
      {
        header: "Applicant Name",
        accessorKey: "applicantName",
        enableColumnFilter: false,
      },
      {
        header: "Applicant Surname",
        accessorKey: "applicantSurname",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
      },
      {
        header: "Created date",
        accessorKey: "createdAt",
        cell: (cellProps) => cellProps.row.original.createdAt 
          ? new Date(cellProps.row.original.createdAt).toLocaleDateString() 
          : 'N/A',
        enableColumnFilter: false,
      }
    ],
    []
  );

  if (loading) {
    return (
      <Container fluid className="text-center mt-5">
        <Spinner color="primary" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid className="text-center mt-5">
        <h5>User not found</h5>
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={`User/${user.id}`}
          breadcrumbItem="User details"
        />

        <Row>
          {/* First Section: User Details */}
          <Col lg="6">
            <Card>
              <CardBody>
                <CardTitle className="mb-4">User Details</CardTitle>

                {/* Profile Image Section */}
                <Row className="mb-3">
                  <Col md="12" className="text-center">
                    <img
                      src={user.image ? resolveImage(user.image) : Image1}
                      alt="User Profile"
                      className="rounded-circle"
                      style={{ width: '150px', height: '150px', objectFit: 'cover', border: '2px solid #ccc' }}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>First Name</Label>
                    <Input type="text" value={user.firstname} disabled />
                  </Col>
                  <Col md="6">
                    <Label>Last Name</Label>
                    <Input type="text" value={user.lastname} disabled />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Label>Email</Label>
                    <Input type="text" value={user.email} disabled />
                  </Col>
                  <Col md="6">
                    <Label>Phone</Label>
                    <Input type="text" value={user.phone} disabled />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Label>Birthdate</Label>
                    <Input
                      type="text"
                      value={user.birthdate ? new Date(user.birthdate).toLocaleDateString() : 'N/A'}
                      disabled
                    />
                  </Col>
                  <Col md="6">
                    <Label>Birthplace</Label>
                    <Input type="text" value={user.birthplace || 'N/A'} disabled />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Label>Wilaya</Label>
                    <Input type="text" value={user.wilaya || 'N/A'} disabled />
                  </Col>
                  <Col md="6">
                    <Label>Gender</Label>
                    <Input type="text" value={user.gender || 'N/A'} disabled />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Label>Created At</Label>
                    <Input type="text" value={new Date(user.createdAt).toLocaleDateString()} disabled />
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col md="12" className="text-center">
                    <Button
                      color={user.available ? 'danger' : 'success'}
                      onClick={toggleAvailability}
                      disabled={updating}
                    >
                      {updating
                        ? "Updating..."
                        : user.available
                        ? "Mark as Unavailable"
                        : "Mark as Available"}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Second Section: Support Tickets */}
          <Col lg="6">
          <Card>
              <CardBody>
                <CardTitle className="mb-4">Visas</CardTitle>
                {user.visas && user.visas.length > 0 ? (
                  <TableContainer
                    columns={visaColumns}
                    data={user.visas}
                    isLoading={loading}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    title=""
                  />
                ) : (
                  <p>No visas found.</p>
                )}
              </CardBody>
            </Card>
            
          </Col>

          {/* New Section: Reservations */}
          <Col lg="12" className="mt-4">
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Reservations</CardTitle>
                {user.reservations && user.reservations.length > 0 ? (
                  <TableContainer
                    columns={reservationColumns}
                    data={user.reservations}
                    isLoading={loading}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    title=""
                  />
                ) : (
                  <p>No reservations found.</p>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* New Section: Visas */}
          <Col lg="12" className="mt-4">
          <Card>
              <CardBody>
                <CardTitle className="mb-4">Support Tickets</CardTitle>
                {user.supportTickets && user.supportTickets.length > 0 ? (
                  <TableContainer
                    columns={supportTicketColumns}
                    data={user.supportTickets}
                    isLoading={loading}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    title=""
                  />
                ) : (
                  <p>No support tickets found.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SingleUser;